import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  RiAddLine,
  RiDeleteBinLine,
  RiCalendarLine,
  RiMailLine,
  RiPhoneLine,
  RiMapPinLine,
  RiUserLine,
  RiArrowLeftLine,
  RiSendPlaneLine,
  RiFileTextLine,
  RiFileListLine,
  RiInformationLine,
  RiListCheck,
  RiPriceTag3Line,
} from "react-icons/ri";
import InvoiceConfirmation from "../../components/billing/InvoiceConfirmation";

const CreateInvoice = () => {
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [invoiceData, setInvoiceData] = useState({
    // Customer Information
    customerName: "",
    email: "",
    phone: "",
    address: "",

    // Invoice Details
    invoiceNumber: `INV-${Date.now().toString().slice(-6)}`,
    invoiceDate: new Date().toISOString().split("T")[0],
    dueDate: "",

    // Company Information
    companyDetails: {
      name: "Your Company Name",
      address: "123 Business Street",
      city: "City, State 12345",
      phone: "+1 234 567 890",
      email: "company@example.com",
      website: "www.yourcompany.com",
    },

    // Invoice Items
    items: [{ description: "", quantity: 1, price: 0, tax: 0 }],

    // Additional Details
    notes: "",
    terms: "Payment is due within 30 days",
  });

  const addItem = () => {
    setInvoiceData({
      ...invoiceData,
      items: [
        ...invoiceData.items,
        { description: "", quantity: 1, price: 0, tax: 0 },
      ],
    });
  };

  const removeItem = (index) => {
    if (invoiceData.items.length > 1) {
      const newItems = invoiceData.items.filter((_, i) => i !== index);
      setInvoiceData({ ...invoiceData, items: newItems });
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...invoiceData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setInvoiceData({ ...invoiceData, items: newItems });
  };

  const calculateSubtotal = () => {
    return invoiceData.items.reduce(
      (total, item) => total + item.quantity * item.price,
      0,
    );
  };

  const calculateTax = () => {
    return invoiceData.items.reduce(
      (total, item) => total + (item.quantity * item.price * item.tax) / 100,
      0,
    );
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isFormValid()) {
      setShowConfirmation(true);
    }
  };

  const handleProceedToPayment = () => {
    setShowConfirmation(false);
    navigate("/billing/payment", {
      state: {
        invoiceData,
        total: calculateTotal(),
      },
    });
  };

  const isFormValid = () => {
    return (
      invoiceData.customerName &&
      invoiceData.email &&
      invoiceData.dueDate &&
      invoiceData.items.every((item) => item.description && item.price > 0)
    );
  };

  const sections = [
    {
      id: "customer",
      title: "Customer Information",
      icon: RiUserLine,
      description: "Enter customer details",
    },
    {
      id: "invoice",
      title: "Invoice Details",
      icon: RiFileTextLine,
      description: "Set invoice information",
    },
    {
      id: "items",
      title: "Invoice Items",
      icon: RiListCheck,
      description: "Add products or services",
    },
    {
      id: "additional",
      title: "Additional Information",
      icon: RiInformationLine,
      description: "Add notes and terms",
    },
  ];

  return (
    <div className="max-w-full mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* <button
            onClick={() => navigate("/billing")}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
          >
            <RiArrowLeftLine className="w-5 h-5" />
          </button> */}
          <h1 className="text-3xl font-bold text-gray-900">
            Create New Invoice
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Information */}
            <div className="card">
              <div className="flex items-center gap-3 mb-6">
                <RiUserLine className="w-6 h-6 text-primary-600" />
                <div>
                  <h2 className="text-xl font-semibold">
                    Customer Information
                  </h2>
                  <p className="text-sm text-gray-500">
                    Enter customer details for the invoice
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Customer Name *</label>
                  <div className="relative">
                    <input
                      type="text"
                      className="input pl-4"
                      placeholder="Enter customer name"
                      value={invoiceData.customerName}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          customerName: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="label">Email *</label>
                  <div className="relative">
                    <RiMailLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      className="input pl-10"
                      placeholder="Enter email address"
                      value={invoiceData.email}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          email: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="label">Phone</label>
                  <div className="relative">
                    <RiPhoneLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      className="input pl-10"
                      placeholder="Enter phone number"
                      value={invoiceData.phone}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          phone: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="label">Address</label>
                  <div className="relative">
                    <RiMapPinLine className="absolute left-3 top-3 text-gray-400" />
                    <textarea
                      className="input pl-10"
                      placeholder="Enter address"
                      value={invoiceData.address}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          address: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Items */}
            <div className="card">
              <div className="flex items-center gap-3 mb-6">
                <RiListCheck className="w-6 h-6 text-primary-600" />
                <div>
                  <h2 className="text-xl font-semibold">Invoice Items</h2>
                  <p className="text-sm text-gray-500">
                    Add products or services to invoice
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <RiFileListLine className="text-gray-400" />
                  Invoice Items
                </h2>
                <button
                  type="button"
                  onClick={addItem}
                  className="btn btn-secondary btn-sm"
                >
                  <RiAddLine className="w-5 h-5" />
                  Add Item
                </button>
              </div>

              {/* Headers */}
              <div className="grid grid-cols-12 gap-4 mb-2 text-sm font-medium text-gray-500">
                <div className="col-span-5">Description</div>
                <div className="col-span-2">Quantity</div>
                <div className="col-span-2">Price</div>
                <div className="col-span-2">Tax %</div>
                <div className="col-span-1"></div>
              </div>

              <div className="space-y-4">
                {invoiceData.items.map((item, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-12 gap-4 items-start"
                  >
                    <div className="col-span-5">
                      <input
                        type="text"
                        className="input"
                        placeholder="Item description"
                        value={item.description}
                        onChange={(e) =>
                          updateItem(index, "description", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        className="input"
                        placeholder="Qty"
                        min="1"
                        value={item.quantity}
                        onChange={(e) =>
                          updateItem(
                            index,
                            "quantity",
                            parseInt(e.target.value) || 0,
                          )
                        }
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        className="input"
                        placeholder="Price"
                        min="0"
                        step="0.01"
                        value={item.price}
                        onChange={(e) =>
                          updateItem(
                            index,
                            "price",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        className="input"
                        placeholder="Tax %"
                        min="0"
                        max="100"
                        value={item.tax}
                        onChange={(e) =>
                          updateItem(
                            index,
                            "tax",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                      />
                    </div>
                    <div className="col-span-1">
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="btn btn-icon btn-error"
                        disabled={invoiceData.items.length === 1}
                      >
                        <RiDeleteBinLine className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Invoice Details */}
            <div className="card">
              <div className="flex items-center gap-3 mb-6">
                <RiFileTextLine className="w-6 h-6 text-primary-600" />
                <div>
                  <h2 className="text-xl font-semibold">Invoice Details</h2>
                  <p className="text-sm text-gray-500">
                    Set invoice number and dates
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="label">Invoice Number</label>
                  <input
                    type="text"
                    className="input bg-gray-50"
                    value={invoiceData.invoiceNumber}
                    readOnly
                  />
                </div>
                <div>
                  <label className="label">Invoice Date</label>
                  <div className="relative">
                    <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="date"
                      className="input pl-10"
                      value={invoiceData.invoiceDate}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          invoiceDate: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="label">Due Date *</label>
                  <div className="relative">
                    <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="date"
                      className="input pl-10"
                      value={invoiceData.dueDate}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          dueDate: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="card">
              <div className="flex items-center gap-3 mb-6">
                <RiInformationLine className="w-6 h-6 text-primary-600" />
                <div>
                  <h2 className="text-xl font-semibold">
                    Additional Information
                  </h2>
                  <p className="text-sm text-gray-500">
                    Add notes and terms to the invoice
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="label">Notes</label>
                  <textarea
                    className="input"
                    placeholder="Add any notes for the customer"
                    value={invoiceData.notes}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, notes: e.target.value })
                    }
                    rows={3}
                  />
                </div>
                <div>
                  <label className="label">Terms & Conditions</label>
                  <textarea
                    className="input"
                    placeholder="Add terms and conditions"
                    value={invoiceData.terms}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, terms: e.target.value })
                    }
                    rows={3}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Summary Section */}
          <div className="lg:col-span-1 space-y-6">
            <div className="card sticky top-6">
              <h2 className="text-xl font-semibold mb-4">Invoice Summary</h2>
              <div className="space-y-4">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>${calculateSubtotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Tax</span>
                  <span>${calculateTax().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-semibold border-t pt-4">
                  <span>Total</span>
                  <span>${calculateTotal().toFixed(2)}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 mt-6">
                <button
                  type="submit"
                  className="btn btn-primary w-full flex items-center justify-center gap-2"
                  disabled={!isFormValid()}
                >
                  <RiSendPlaneLine className="w-5 h-5" />
                  Generate Invoice
                </button>
                <button type="button" className="btn btn-secondary w-full">
                  Save as Draft
                </button>
              </div>

              {/* Quick Tips */}
              <div className="mt-6 p-4 bg-primary-50 rounded-lg">
                <h3 className="font-semibold text-primary-900 mb-2">
                  Quick Tips
                </h3>
                <ul className="text-sm text-primary-700 space-y-2">
                  <li>• All required fields are marked with *</li>
                  <li>• You can add multiple items to the invoice</li>
                  <li>• Tax is calculated per item</li>
                  <li>• Save as draft to edit later</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </form>

      {showConfirmation && (
        <InvoiceConfirmation
          invoice={invoiceData}
          onConfirm={handleProceedToPayment}
          onCancel={() => setShowConfirmation(false)}
        />
      )}
    </div>
  );
};

export default CreateInvoice;
